import axios from 'axios'
import { appConfig } from '../config'

const authHeaders = (token) => ({
  Authorization: `Bearer ${token}`
})

const base = () => `${appConfig.apiURL}/api/socr`

const unwrapError = (err, fallback) => {
  const status = err?.response?.status
  const data = err?.response?.data
  const detail =
    (typeof data === 'string' && data) ||
    data?.title ||
    data?.detail ||
    data?.message ||
    (Array.isArray(data?.errors) ? data.errors.join('; ') : null) ||
    err?.message
  const message = detail || fallback
  const error = new Error(message)
  error.status = status
  error.payload = data
  return error
}

const request = async (token, config, fallback) => {
  try {
    const res = await axios({
      ...config,
      headers: {
        ...authHeaders(token),
        ...(config.headers || {})
      }
    })
    return res.data
  } catch (err) {
    throw unwrapError(err, fallback)
  }
}

/** Strip Bitbucket UUID braces for compare/store. */
export const normalizeRepoUuid = (uuid) => String(uuid || '').replace(/[{}]/g, '')

/** GET /api/socr/clients?q= */
export const listSocrClients = (token, { q } = {}) =>
  request(
    token,
    {
      method: 'GET',
      url: `${base()}/clients`,
      params: q ? { q } : undefined
    },
    'Failed to list clients'
  )

/** GET /api/socr/clients/{id} */
export const getSocrClient = (token, id) =>
  request(
    token,
    { method: 'GET', url: `${base()}/clients/${encodeURIComponent(id)}` },
    'Failed to load client'
  )

/** POST /api/socr/clients */
export const createSocrClient = (token, body) =>
  request(
    token,
    { method: 'POST', url: `${base()}/clients`, data: body },
    'Failed to create client'
  )

/** PUT /api/socr/clients/{id} */
export const updateSocrClient = (token, id, body) =>
  request(
    token,
    {
      method: 'PUT',
      url: `${base()}/clients/${encodeURIComponent(id)}`,
      data: body
    },
    'Failed to update client'
  )

/** DELETE /api/socr/clients/{id} */
export const deleteSocrClient = (token, id) =>
  request(
    token,
    {
      method: 'DELETE',
      url: `${base()}/clients/${encodeURIComponent(id)}`
    },
    'Failed to delete client'
  )

/** PUT /api/socr/clients/{id}/jira — full replace */
export const replaceSocrJira = (token, id, links) =>
  request(
    token,
    {
      method: 'PUT',
      url: `${base()}/clients/${encodeURIComponent(id)}/jira`,
      data: links
    },
    'Failed to update Jira links'
  )

/** PUT /api/socr/clients/{id}/bitbucket — full replace */
export const replaceSocrBitbucket = (token, id, links) =>
  request(
    token,
    {
      method: 'PUT',
      url: `${base()}/clients/${encodeURIComponent(id)}/bitbucket`,
      data: links
    },
    'Failed to update Bitbucket links'
  )

/** PUT /api/socr/clients/{id}/qbo — full replace */
export const replaceSocrQbo = (token, id, customers) =>
  request(
    token,
    {
      method: 'PUT',
      url: `${base()}/clients/${encodeURIComponent(id)}/qbo`,
      data: customers
    },
    'Failed to update QBO links'
  )

/** PUT /api/socr/clients/{id}/google — full replace */
export const replaceSocrGoogle = (token, id, projectIds) =>
  request(
    token,
    {
      method: 'PUT',
      url: `${base()}/clients/${encodeURIComponent(id)}/google`,
      data: projectIds
    },
    'Failed to update Google links'
  )

/** GET /api/socr/relationships?type= */
export const listSocrRelationships = (token, { type } = {}) =>
  request(
    token,
    {
      method: 'GET',
      url: `${base()}/relationships`,
      params: type ? { type } : undefined
    },
    'Failed to list relationships'
  )

/** POST /api/socr/relationships */
export const createSocrRelationship = (token, body) =>
  request(
    token,
    { method: 'POST', url: `${base()}/relationships`, data: body },
    'Failed to create relationship'
  )

/** DELETE /api/socr/relationships/{id} */
export const deleteSocrRelationship = (token, id) =>
  request(
    token,
    {
      method: 'DELETE',
      url: `${base()}/relationships/${encodeURIComponent(id)}`
    },
    'Failed to delete relationship'
  )

/** GET /api/socr/repositories?q= — inventory for Bitbucket mapping */
export const listSocrRepositories = (token, { q } = {}) =>
  request(
    token,
    {
      method: 'GET',
      url: `${base()}/repositories`,
      params: q ? { q } : undefined
    },
    'Failed to list repositories'
  )

/**
 * POST /api/socr/repositories/refresh-dt
 * One DT fetch → sync repository_dt_projects for all linked repos (scope: "all").
 */
export const refreshSocrDtProjects = (token) =>
  request(
    token,
    {
      method: 'POST',
      url: `${base()}/repositories/refresh-dt`
    },
    'Failed to refresh DT projects into SOCR'
  )

/**
 * POST /api/socr/repositories/{uuid}/refresh-dt
 * Same sync as refresh-all, scoped to one catalog repo (scope: "one"). 404 if UUID unknown.
 */
export const refreshSocrRepositoryDt = (token, uuid) =>
  request(
    token,
    {
      method: 'POST',
      url: `${base()}/repositories/${encodeURIComponent(normalizeRepoUuid(uuid))}/refresh-dt`
    },
    'Failed to refresh DT projects for repository'
  )

/** Metadata sync should finish well under typical proxy idle limits. */
const BITBUCKET_METADATA_TIMEOUT_MS = 30 * 1000
/**
 * Do not hold the browser socket for multi-minute .csproj scans (ALB/CF 504 + pool exhaustion).
 * Server continues after client abort; callers should reload code-tree / poll instead.
 */
const BITBUCKET_RUNTIME_CLIENT_WAIT_MS = 45 * 1000

/**
 * Build refresh-bitbucket URL. Default = metadata only (name/slug).
 * Pass includeRuntimes for the slow default-branch .csproj / TFM rescan.
 */
const refreshBitbucketUrl = (uuid, { includeRuntimes = false } = {}) => {
  const path = uuid
    ? `${base()}/repositories/${encodeURIComponent(normalizeRepoUuid(uuid))}/refresh-bitbucket`
    : `${base()}/repositories/refresh-bitbucket`
  const params = new URLSearchParams()
  if (includeRuntimes) params.set('includeRuntimes', 'true')
  const qs = params.toString()
  return qs ? `${path}?${qs}` : path
}

/** True when the client/proxy dropped the wait (scan may still run server-side). */
export const isBitbucketRefreshClientDisconnect = (err) => {
  const status = err?.status ?? err?.response?.status
  if (status === 504 || status === 502 || status === 408) return true
  const code = err?.code || err?.payload?.code
  if (code === 'ERR_CANCELED' || code === 'ECONNABORTED' || code === 'ETIMEDOUT') return true
  const name = err?.name || ''
  if (name === 'CanceledError' || name === 'AbortError') return true
  return /timeout|aborted|canceled|cancelled|gateway/i.test(String(err?.message || ''))
}

const mergeAbortSignals = (signals) => {
  const list = (signals || []).filter(Boolean)
  if (!list.length) return undefined
  if (typeof AbortSignal !== 'undefined' && typeof AbortSignal.any === 'function') {
    return AbortSignal.any(list)
  }
  const controller = new AbortController()
  const onAbort = () => controller.abort()
  for (const signal of list) {
    if (signal.aborted) {
      controller.abort()
      break
    }
    signal.addEventListener('abort', onAbort, { once: true })
  }
  return controller.signal
}

/**
 * POST /api/socr/repositories/refresh-bitbucket
 * Default: Bitbucket name/slug metadata for every SOCR catalog repo (scope: "all").
 * includeRuntimes uses a short client wait + AbortSignal (not a multi-minute sync hold).
 * @param {{ includeRuntimes?: boolean, signal?: AbortSignal }} [opts]
 */
export const refreshSocrBitbucket = (token, { includeRuntimes = false, signal } = {}) => {
  const waitSignal = includeRuntimes
    ? mergeAbortSignals([signal, AbortSignal.timeout(BITBUCKET_RUNTIME_CLIENT_WAIT_MS)])
    : signal
  return request(
    token,
    {
      method: 'POST',
      url: refreshBitbucketUrl(null, { includeRuntimes }),
      timeout: includeRuntimes ? 0 : BITBUCKET_METADATA_TIMEOUT_MS,
      signal: waitSignal
    },
    'Failed to refresh Bitbucket repositories into SOCR'
  )
}

/**
 * POST /api/socr/repositories/{uuid}/refresh-bitbucket[?includeRuntimes=true]
 * Same as refresh-all, scoped to one catalog repo (scope: "one"). 404 if UUID unknown.
 * @param {{ includeRuntimes?: boolean, signal?: AbortSignal }} [opts]
 */
export const refreshSocrRepositoryBitbucket = (token, uuid, { includeRuntimes = false, signal } = {}) => {
  const waitSignal = includeRuntimes
    ? mergeAbortSignals([signal, AbortSignal.timeout(BITBUCKET_RUNTIME_CLIENT_WAIT_MS)])
    : signal
  return request(
    token,
    {
      method: 'POST',
      url: refreshBitbucketUrl(uuid, { includeRuntimes }),
      timeout: includeRuntimes ? 0 : BITBUCKET_METADATA_TIMEOUT_MS,
      signal: waitSignal
    },
    'Failed to refresh Bitbucket repository'
  )
}

/** POST /api/socr/repositories/resolve — Bitbucket URL → catalog upsert */
export const resolveSocrRepository = (token, { url }) =>
  request(
    token,
    {
      method: 'POST',
      url: `${base()}/repositories/resolve`,
      data: { url }
    },
    'Failed to resolve Bitbucket repository'
  )

/** POST /api/socr/jira/resolve — Jira URL or bare key */
export const resolveSocrJira = (token, { url }) =>
  request(
    token,
    {
      method: 'POST',
      url: `${base()}/jira/resolve`,
      data: { url }
    },
    'Failed to resolve Jira project'
  )

/** GET /api/socr/repositories/{uuid} */
export const getSocrRepository = (token, uuid) =>
  request(
    token,
    {
      method: 'GET',
      url: `${base()}/repositories/${encodeURIComponent(String(uuid || '').replace(/[{}]/g, ''))}`
    },
    'Failed to load repository'
  )

/** GET /api/socr/repositories/{uuid}/branches */
export const listRepoBranches = (token, uuid) =>
  request(
    token,
    {
      method: 'GET',
      url: `${base()}/repositories/${encodeURIComponent(String(uuid || '').replace(/[{}]/g, ''))}/branches`
    },
    'Failed to list branches'
  )

/** GET /api/socr/repositories/{uuid}/branches/{branch}/pipeline-config */
export const getBranchPipelineConfig = (token, uuid, branch) =>
  request(
    token,
    {
      method: 'GET',
      url: `${base()}/repositories/${encodeURIComponent(String(uuid || '').replace(/[{}]/g, ''))}/branches/${encodeURIComponent(branch)}/pipeline-config`
    },
    'Failed to load pipeline config'
  )

/**
 * POST /api/socr/repositories/{uuid}/dt-search
 * @param {{ branch: string, createMissing?: boolean, updateInventory?: boolean }} body
 */
export const searchRepoDt = (token, uuid, body) =>
  request(
    token,
    {
      method: 'POST',
      url: `${base()}/repositories/${encodeURIComponent(String(uuid || '').replace(/[{}]/g, ''))}/dt-search`,
      data: body
    },
    'Failed to search DT projects for branch'
  )

/** POST /api/socr/repositories/{uuid}/pipelines/trigger — kick Bitbucket CI SBOM build */
export const triggerRepoPipeline = (token, uuid, { branch }) =>
  request(
    token,
    {
      method: 'POST',
      url: `${base()}/repositories/${encodeURIComponent(String(uuid || '').replace(/[{}]/g, ''))}/pipelines/trigger`,
      data: { branch }
    },
    'Failed to trigger pipeline'
  )

export const RELATIONSHIP_TYPES = [
  { value: 'shared_codebase', label: 'Shared codebase' },
  { value: 'shared_billing', label: 'Shared billing' },
  { value: 'shared_google', label: 'Shared Google' }
]
